import { Link, useRouterState } from '@tanstack/react-router'
import { useSession } from '@ventawiz/web-kit'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { StoreZoneRead } from '@/client'
import { Picker } from '@/components/common/Picker'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useClearTimezone, useMyTimezones, useSetTimezone } from '@/hooks/queries/timezones'
import { useResolvedTimezone } from '@/hooks/useResolvedTimezone'
import { useActiveTimezoneScope, useRouteZone } from '@/hooks/useZone'
import {
  TIMEZONE_SOURCE_LABELS,
  browserTimezone,
  fallbackFor,
  overrideFor,
  timezoneFlag,
  timezoneOptions,
  type ResolveInput,
  type StoreZoneKind,
  type TimezoneSource,
} from '@/lib/timezone'
import { cn } from '@/lib/utils'

import { routeZone } from './timezoneMap'

/** Where the list of every page's zones lives. */
const TIME_ZONES_PATH = '/time-zones'

/** What a picker's empty option says, by where the page falls back to without it. */
const FALLBACK_LABELS = {
  'store-page': 'common.timezone.popover.fallback.default',
  page: 'common.timezone.popover.fallback.page',
  profile: 'common.timezone.popover.fallback.default',
  'store-zone': 'common.timezone.popover.fallback.storeZone',
  'ads-zone': 'common.timezone.popover.fallback.adsZone',
  browser: 'common.timezone.popover.fallback.default',
} as const satisfies Record<TimezoneSource, string>

/**
 * "Timezone: Europe/Berlin" in the header: the zone the page on screen reads
 * its times in, following the reader from page to page.
 *
 * Clicking it opens a small box for that page: its zone for all stores and,
 * when the page shows a store, its zone for that store — set right there, and
 * the badge follows at once. "All pages" opens the list of every page's zones.
 *
 * The page comes from the sidebar (`timezoneMap.ts`); a page that shows a
 * store adds that store through its `TimezoneScope`.
 */
export function TimezoneButton({ pathname }: { pathname: string }) {
  const { t } = useTranslation()
  const route = useRouteZone()
  const active = useActiveTimezoneScope()
  const [open, setOpen] = useState(false)
  // On the time zones list itself the badge reads the page the reader came
  // from, so it changes as they set that page's zones.
  const here = pathname === TIME_ZONES_PATH
  const search = useRouterState({ select: (state) => state.location.search }) as {
    from?: unknown
    store?: unknown
    ads?: unknown
  }
  const from = here && typeof search.from === 'string' ? routeZone(search.from) : undefined
  const fromStore = typeof search.store === 'number' ? search.store : undefined
  const resolved = useResolvedTimezone(
    from
      ? { page: from.page, storeId: fromStore, kind: search.ads === true ? 'ads' : 'store' }
      : { page: route.page, storeId: active?.storeId, kind: active?.kind ?? 'store' },
  )

  if (resolved.pending) {
    return (
      <span aria-busy="true" className="inline-flex items-center">
        <span className="sr-only">{t('common.timezone.loading')}</span>
        <Skeleton className="h-9 w-36" />
      </span>
    )
  }

  const why = t(TIMEZONE_SOURCE_LABELS[resolved.source], {
    store: resolved.store?.name ?? '',
  })
  const face = (
    <>
      {timezoneFlag(resolved.zone) ? (
        <span aria-hidden="true">{timezoneFlag(resolved.zone)}</span>
      ) : (
        <Icon name="public" size={16} className="text-muted-foreground" />
      )}
      <span className="text-muted-foreground hidden lg:inline">{t('common.timezone.label')}</span>
      <span className="max-w-40 truncate font-medium">{resolved.zone}</span>
    </>
  )
  const listSearch = {
    from: pathname,
    ...(active?.storeId !== undefined ? { store: active.storeId } : {}),
    ...(active?.kind === 'ads' ? { ads: true } : {}),
  }

  // Off the sidebar's pages — the list itself, My access — there is no page
  // to set, so the badge is the way to the list.
  if (route.page === null) {
    return (
      <Button
        asChild
        variant="outline"
        size="sm"
        className={cn('h-9 gap-1.5 px-2.5', here && 'bg-surface-muted')}
      >
        <Link
          to={TIME_ZONES_PATH}
          search={here ? true : listSearch}
          aria-label={t('common.timezone.button', { zone: resolved.zone })}
          aria-current={here ? 'page' : undefined}
          title={why}
        >
          {face}
        </Link>
      </Button>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 px-2.5"
          aria-label={t('common.timezone.button', { zone: resolved.zone })}
          title={why}
        >
          {face}
          <Icon name="expand_more" size={16} className="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <PageZones
          page={route.page}
          pageLabel={route.pageLabel ? t(route.pageLabel) : route.page}
          storeId={active?.storeId}
          store={resolved.store}
          kind={active?.kind ?? 'store'}
          allPages={
            <Link
              to={TIME_ZONES_PATH}
              search={listSearch}
              onClick={() => setOpen(false)}
              className="text-primary-text inline-flex items-center gap-1 text-sm font-medium hover:underline"
            >
              {t('common.timezone.popover.allPages')}
              <Icon name="arrow_forward" size={16} />
            </Link>
          }
        />
      </PopoverContent>
    </Popover>
  )
}

/** The box's body: the page's zone for all stores, for the store it shows, and Reset. */
function PageZones({
  page,
  pageLabel,
  storeId,
  store,
  kind,
  allPages,
}: {
  page: string
  pageLabel: string
  storeId: number | undefined
  store: StoreZoneRead | undefined
  kind: StoreZoneKind
  allPages: ReactNode
}) {
  const { t } = useTranslation()
  const { session } = useSession()
  const mine = useMyTimezones()
  const set = useSetTimezone()
  const clear = useClearTimezone()
  const overrides = mine.data?.overrides ?? []

  const input = (withStore: boolean): ResolveInput => ({
    page,
    storeId: withStore ? storeId : undefined,
    kind,
    overrides,
    profile: session?.principal?.timezone ?? undefined,
    store: withStore ? store : undefined,
    browser: browserTimezone(),
  })
  const busy = (level: number | null) => {
    const pending = set.isPending ? set.variables : clear.isPending ? clear.variables : undefined
    return pending !== undefined && (pending.store_id ?? null) === level && pending.page === page
  }
  const save = (level: number | null, zone: string | undefined) => {
    if (zone !== undefined) set.mutate({ store_id: level, page, timezone: zone })
    else if (overrideFor(overrides, level, page)) clear.mutate({ store_id: level, page })
  }

  const levels: { storeId: number | null; label: string; input: ResolveInput }[] = [
    { storeId: null, label: t('common.timezone.popover.allStores'), input: input(false) },
    ...(storeId !== undefined
      ? [
          {
            storeId,
            label: store?.name ?? t('common.timezone.unknownStore', { id: storeId }),
            input: input(true),
          },
        ]
      : []),
  ]
  const anySet = levels.some((level) => overrideFor(overrides, level.storeId, page))

  return (
    <div className="flex flex-col">
      <p className="border-b px-4 py-3 text-sm font-semibold">{pageLabel}</p>
      <div className="flex flex-col gap-3 px-4 py-3">
        {levels.map((level) => {
          const value = overrideFor(overrides, level.storeId, page)?.timezone
          const fallback = fallbackFor(level.input, { storeId: level.storeId, page })
          return (
            <div
              key={level.storeId ?? 'all'}
              className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2"
            >
              <span className="truncate text-sm" title={level.label}>
                {level.label}
              </span>
              <Picker
                label={t('common.timezone.popover.zoneFor', { label: level.label })}
                hideLabel
                size="sm"
                options={timezoneOptions(value)}
                value={value}
                onChange={(zone) => save(level.storeId, zone)}
                emptyLabel={t(FALLBACK_LABELS[fallback.source], { zone: fallback.zone })}
                searchLabel={t('common.timezone.search')}
                noMatches={t('common.timezone.noMatches')}
                pending={busy(level.storeId)}
              />
            </div>
          )
        })}
        {set.isError || clear.isError ? (
          <p role="alert" className="text-error text-xs">
            {t('common.timezone.failed')}
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between border-t px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={!anySet || set.isPending || clear.isPending}
          onClick={() => {
            for (const level of levels) {
              if (overrideFor(overrides, level.storeId, page)) {
                clear.mutate({ store_id: level.storeId, page })
              }
            }
          }}
        >
          {t('common.timezone.popover.reset')}
        </Button>
        {allPages}
      </div>
    </div>
  )
}
